import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import NavigationUser from '../../Layouts/Navigation/NavigationUser';
import { Container, Typography, Box, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, TablePagination, Button, Dialog, 
    DialogContent, DialogTitle, Snackbar, Alert } from '@mui/material';
import { format } from 'date-fns';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { BASE_URL } from '../../utils/constants';
import useAutoDismissSnack from '../../Hooks/AutoDismissSnack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';


function ShipmentsUsrReport() {  

    const [paqueteria, setPaqueteria] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openDialog, setOpenDialog] = useState(false);
    const [packageInfo, setPackageInfo] = useState(null);
    const [InquilinoId, setInquilinoId] = useState(null);

    //Snack notifications
    const [openSnack, setOpenSnack] = React.useState(false);
    const [snackStatus, setSnackStatus] = useState("");
    const [snackMessage, setSnackMessage] = useState("");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const getRowColor = (status) => {
        switch (status) {
            case 'Entregado':
                return '#ddffdd';
            case 'Pendiente':
                return '#fff4ec';
            default:
                return 'inherit';
        }
    };

    const handleClickSnack = () => {
        setOpenSnack(true);
    };

    const handleCloseSnack = () => {
        setOpenSnack(false);
    };
    useAutoDismissSnack(openSnack, setOpenSnack);
    
    const handleClose = () => {
        setOpenDialog(false);
    };

    const formatDate = (dateString) => {
        return format(new Date(dateString), 'yyyy-MM-dd hh:mm:ss a');
    };

    const getPaqueteriaByIdInquilino = () => {
        axios.get(`${BASE_URL}/getPaqueteriaByIdInquilino/${InquilinoId}`)
        .then((response) => {
            setPaqueteria(response.data);
        })
        .catch((error) => {
            console.log(error);
        });
    };
  
    const handleInfoPackage = (paquete) => {
        setPackageInfo(paquete); // Actualiza la información del paquete
        setOpenDialog(true); // Abre el diálogo
    };

    // Efecto para obtener el ID del usuario del token (solo se ejecuta una vez al montar)
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const decodedToken = jwtDecode(token);
            setInquilinoId(decodedToken.id_usuario);
        } else {
            console.log('No hay token');
        }
    }, []);

    // Efecto para cargar paquetes cuando InquilinoId esté disponible
    useEffect(() => {
        if (InquilinoId) {
            getPaqueteriaByIdInquilino();
        }
    }, [InquilinoId]);

    // Calcular estadísticas para las gráficas
    const packageStats = {
        total: paqueteria.length,
        entregados: paqueteria.filter(p => p.estado === 'Entregado').length,
        pendientes: paqueteria.filter(p => p.estado === 'Pendiente').length
    };

    const packageChartData = [
        { name: 'Entregados', value: packageStats.entregados, color: '#10b981' },
        { name: 'Pendientes', value: packageStats.pendientes, color: '#f59e0b' }
    ];

    // Datos para gráfica por tipo de paquete
    const packageTypeData = paqueteria.reduce((acc, pkg) => {
        const tipo = pkg.tipo_paquete || 'Sin tipo';
        const existing = acc.find(item => item.name === tipo);
        if (existing) {
            existing.value += 1;
        } else {
            acc.push({ name: tipo, value: 1 });
        }
        return acc;
    }, []);

    const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

    return (
        <>
            <NavigationUser/>
            
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Reporte de Paquetería</h1>
                        <p className="text-gray-600">Historial completo de todos tus paquetes recibidos</p>
                    </div>

                    {/* Estadísticas rápidas */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Total Paquetes</p>
                                    <p className="text-3xl font-bold text-purple-600">{packageStats.total}</p>
                                </div>
                                <div className="p-3 bg-purple-100 rounded-lg">
                                    <span className="text-2xl">📦</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Entregados</p>
                                    <p className="text-3xl font-bold text-green-600">{packageStats.entregados}</p>
                                </div>
                                <div className="p-3 bg-green-100 rounded-lg">
                                    <span className="text-2xl">✅</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Pendientes</p>
                                    <p className="text-3xl font-bold text-amber-600">{packageStats.pendientes}</p>
                                </div>
                                <div className="p-3 bg-amber-100 rounded-lg">
                                    <span className="text-2xl">⏳</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Gráficas */}
                    {paqueteria.length > 0 && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                            {/* Gráfica de Estado de Paquetes */}
                            <div className="bg-white rounded-xl shadow-md p-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-4">Estado de Paquetes</h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={packageChartData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {packageChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Gráfica por Tipo de Paquete */}
                            <div className="bg-white rounded-xl shadow-md p-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-4">Paquetes por Tipo</h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={packageTypeData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="value" fill="#8b5cf6" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    <Snackbar open={openSnack} autoHideDuration={5000} onClose={handleCloseSnack}>
                        <Alert 
                        onClose={handleCloseSnack} 
                        severity={snackStatus} 
                        sx={{ width: '100%' }}>
                        {snackMessage}
                        </Alert>
                    </Snackbar>

                    {/* Tabla de Paquetes Centrada */}
                    <div className="max-w-6xl mx-auto px-4">
                        {paqueteria.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <span className="text-6xl mb-4 block">📦</span>
                                <p className="text-gray-600 text-lg">No hay paquetes registrados.</p>
                                <p className="text-gray-400 mt-2">Los paquetes que recibas aparecerán aquí</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                                    <Table sx={{ minWidth: 650 }} stickyHeader>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#8b5cf6' }}>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#ffffff', borderBottom: '2px solid #7c3aed' }}>Torre</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#ffffff', borderBottom: '2px solid #7c3aed' }}>Apto.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#ffffff', borderBottom: '2px solid #7c3aed' }}>Nombre</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#ffffff', borderBottom: '2px solid #7c3aed' }}>Fecha ingreso</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#ffffff', borderBottom: '2px solid #7c3aed' }}>Estado</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#ffffff', borderBottom: '2px solid #7c3aed' }}>Información</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paqueteria.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((paquete) => (
                                                <TableRow
                                                    key={paquete.id_paquete}
                                                    sx={{ 
                                                        backgroundColor: getRowColor(paquete.estado), 
                                                        '&:hover': { backgroundColor: '#f3e8ff', transform: 'scale(1.005)' },
                                                        transition: 'all 0.2s',
                                                        '&:last-child td, &:last-child th': { border: 0 } 
                                                    }}
                                                >
                                                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, padding: '16px' }}>
                                                        {paquete.torre_nombre}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 600, padding: '16px' }}>
                                                        {paquete.num_apartamento}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.875rem', padding: '16px' }}>
                                                        {paquete.nombre} {paquete.apellido}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.875rem', padding: '16px' }}>
                                                        {formatDate(paquete.fecha_recibido)}
                                                    </TableCell>
                                                    <TableCell sx={{ padding: '16px' }}>
                                                        {paquete.estado === 'Entregado' ? (
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 shadow-sm">
                                                                ✅ Entregado
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 shadow-sm">
                                                                ⏳ Pendiente
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ padding: '16px' }}>
                                                        <Button 
                                                            variant="outlined" 
                                                            size="small"
                                                            onClick={() => handleInfoPackage(paquete)}
                                                            sx={{
                                                                textTransform: 'none',
                                                                borderRadius: '8px',
                                                                borderColor: '#8b5cf6',
                                                                color: '#8b5cf6',
                                                                fontWeight: 600,
                                                                '&:hover': {
                                                                    backgroundColor: '#f3e8ff',
                                                                    borderColor: '#7c3aed',
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: '0 4px 6px rgba(139, 92, 246, 0.25)'
                                                                },
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            <PostAddIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                                            Ver
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={paqueteria.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={(event, newPage) => {
                                        setPage(newPage);
                                    }}
                                    onRowsPerPageChange={(event) => {
                                        setRowsPerPage(parseInt(event.target.value, 10));
                                        setPage(0);
                                    }}
                                    sx={{
                                        borderTop: '2px solid #e2e8f0',
                                        backgroundColor: '#f9fafb'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* DIALOGO PARA MOSTRAR INFORMACION DE PAQUETE */}
            <Dialog
                fullScreen={fullScreen}
                fullWidth
                maxWidth="md"
                sx={{ 
                    width: { xs: '90%', md: '50%' }, 
                    mx: 'auto',
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }
                }}
                open={openDialog}
                onClose={handleClose}
            >
                <DialogTitle sx={{ 
                    textAlign: 'center', 
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    paddingY: 3,
                    borderRadius: '16px 16px 0 0'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        📦 Detalles del Paquete
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                        Estado: {packageInfo ? (
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${packageInfo.estado === 'Entregado' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                {packageInfo.estado === 'Entregado' ? '✅ ' : '⏳ '} {packageInfo.estado}
                            </span>
                        ) : 'Cargando...'}
                    </Typography>
                    {packageInfo && packageInfo.estado === 'Entregado' && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
                            Fecha de entrega: {formatDate(packageInfo.fecha_entrega)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                    {packageInfo ? (
                        <div className="space-y-4">
                            {/* Información del paquete */}
                            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-purple-100">
                                <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-4 py-3">
                                    <h3 className="text-white font-bold text-sm">📦 Información del Paquete</h3>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Nombre:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.nombre} {packageInfo.apellido}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Fecha Recibido:</span>
                                        <span className="text-sm text-gray-800">{formatDate(packageInfo.fecha_recibido)}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Estado:</span>
                                        <span className="text-sm">
                                            {packageInfo.estado === 'Entregado' ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                    ✅ Entregado
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                                                    ⏳ Pendiente
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Observaciones:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.observacion || 'Sin observaciones'}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Remitente:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.remitente}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Tipo de Paquete:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.tipo_paquete}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Cantidad:</span>
                                        <span className="text-sm text-gray-800 font-bold">{packageInfo.cantidad}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Información del residente */}
                            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-purple-100">
                                <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-4 py-3">
                                    <h3 className="text-white font-bold text-sm">👤 Información del Residente</h3>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Nombre:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.nombre} {packageInfo.apellido}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Teléfono:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.telefono || 'No disponible'}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Correo:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.email}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Torre:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.torre_nombre}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Apartamento:</span>
                                        <span className="text-sm text-gray-800 font-bold">{packageInfo.num_apartamento}</span>
                                    </div>
                                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4">
                                        <span className="text-sm font-semibold text-gray-600">Piso:</span>
                                        <span className="text-sm text-gray-800">{packageInfo.piso}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Firma de recibido */}
                            {packageInfo && packageInfo.estado !== 'Pendiente' && (
                                <div className="bg-white rounded-lg shadow-md overflow-hidden border border-purple-100">
                                    <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-4 py-3">
                                        <h3 className="text-white font-bold text-sm">✍️ Firma de Recibido</h3>
                                    </div>
                                    <div className="p-6 text-center bg-gray-50">
                                        {packageInfo.firma_entrega ? (
                                            <img
                                                src={`${BASE_URL}/${packageInfo.firma_entrega}`}
                                                alt="Firma de entrega"
                                                className="max-w-full max-h-72 mx-auto rounded-lg shadow-sm border border-gray-200"
                                            />
                                        ) : (
                                            <div className="py-8">
                                                <span className="text-gray-400 text-sm">No hay firma de entrega disponible</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Botón cerrar */}
                            <div className="flex justify-center pt-2">
                                <button
                                    onClick={handleClose}
                                    className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-purple-700 hover:to-purple-600 transition-all duration-200 transform hover:-translate-y-0.5"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <Typography>Cargando...</Typography>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

export default ShipmentsUsrReport;